import { InfoCard } from '#app/components/ui/info-card.tsx'
import { type Route } from './+types/convention-info.ts'

type QuickLinkSection = {
	id: string
	label: string
	cards: Array<{ id: string; label: string }>
}

const QUICK_LINK_SECTIONS: QuickLinkSection[] = [
	{
		id: 'ages-fees',
		label: 'Ages & Fees',
		cards: [{ id: 'ages-fees-overview', label: 'Overview Notes' }],
	},
	{
		id: 'registration-details',
		label: 'Registration Details',
		cards: [
			{ id: 'registration-opening', label: 'Registration Opening' },
			{ id: 'important-notice', label: 'Important Notice' },
			{ id: 'registration-process', label: 'Registration Process' },
			{ id: 'registration-kits', label: 'Registration Kits' },
			{ id: 'vip-lanyards', label: 'VIP Lanyards' },
			{ id: 'important-requirements', label: 'Important Requirements' },
			{ id: 'liability-waiver', label: 'Liability Waiver' },
		],
	},
	{
		id: 'payment-information',
		label: 'Payment Information',
		cards: [
			{ id: 'payment-deadlines', label: 'Payment Deadlines' },
			{ id: 'payment-methods', label: 'Payment Methods' },
			{ id: 'refund-policy', label: 'Refund Policy' },
		],
	},
	{
		id: 'discounts-benefits',
		label: 'Discounts & Benefits',
		cards: [
			{ id: 'teacher-benefits', label: 'Teacher Benefits' },
			{ id: 'attendee-discounts', label: 'Attendee Discounts' },
		],
	},
	{
		id: 'event-information',
		label: 'Event Information',
		cards: [
			{ id: 'wristbands-entry', label: 'Wristbands & Entry' },
			{ id: 'ballroom-guidelines', label: 'Ballroom Guidelines' },
			{ id: 'teacher-guidelines', label: 'Teacher Guidelines' },
			{
				id: 'assistant-teacher-guidelines',
				label: 'Assistant Teacher Guidelines',
			},
			{ id: 'etiquette-dress-code', label: 'Etiquette & Dress Code' },
			{ id: 'media-photography', label: 'Media & Photography' },
		],
	},
	{ id: 'questions', label: 'Questions', cards: [] },
]

function QuickLinksPanel() {
	return (
		<div className="border border-muted-foreground/30 bg-muted/30 p-4 sm:p-6">
			<div className="mb-4 flex items-center gap-3 sm:mb-5">
				<div className="flex h-8 w-8 items-center justify-center text-primary">
					<svg
						className="h-5 w-5"
						fill="none"
						viewBox="0 0 24 24"
						stroke="currentColor"
						strokeWidth={2.5}
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
						/>
					</svg>
				</div>
				<h3 className="font-display text-base font-bold tracking-wide sm:text-lg">
					QUICK LINKS
				</h3>
			</div>

			<div className="space-y-3">
				{QUICK_LINK_SECTIONS.map((section, sectionIndex) => (
					<div
						key={section.id}
						className="border border-border/50 bg-background/95 p-3"
					>
						<div className="mb-2 flex items-center gap-2">
							<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-foreground">
								{sectionIndex + 1}
							</span>
							<a
								href={`#${section.id}`}
								className="inline-flex flex-1 items-center border border-border/60 bg-muted/40 px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-foreground transition-colors hover:border-primary/50 hover:text-primary"
							>
								<span>{section.label}</span>
							</a>
						</div>

						{section.cards.length > 0 ? (
							<div className="ml-2 space-y-1.5 border-l border-border/50 pl-3">
								{section.cards.map((card) => (
									<a
										key={card.id}
										href={`#${card.id}`}
										className="group flex items-start gap-2 rounded-sm border border-transparent px-2 py-1.5 text-xs leading-snug text-foreground/85 transition-all hover:border-border/60 hover:bg-muted/40 hover:text-foreground"
									>
										<span className="mt-1 h-1.5 w-1.5 rounded-full bg-primary/60 transition-colors group-hover:bg-primary" />
										<span className="flex-1">{card.label}</span>
									</a>
								))}
							</div>
						) : (
							<p className="ml-2 text-xs text-muted-foreground">
								General section details.
							</p>
						)}
					</div>
				))}
			</div>
		</div>
	)
}

export const meta: Route.MetaFunction = () => {
	const title = 'Convention Guidelines | DanceMakers'
	const description =
		'Everything you need to know about DanceMakers convention including ages, fees, registration details, and event information.'
	const siteUrl = 'https://dancemakersinc.com'

	return [
		{ title },
		{ name: 'description', content: description },
		{ property: 'og:title', content: title },
		{ property: 'og:description', content: description },
		{ property: 'og:url', content: `${siteUrl}/convention-info` },
	]
}

export default function ConventionRoute() {
	return (
		<div className="min-h-screen pt-12 md:pt-16">
			{/* Hero Section */}
			<section className="container px-6 pb-8 pt-12 md:px-16 md:pb-10 md:pt-16">
				<div className="max-w-3xl">
					<p className="mb-3 text-xs font-bold uppercase tracking-[0.2em] text-primary">
						2026-27 Season
					</p>
					<h1 className="mb-6 font-display text-3xl font-bold uppercase leading-tight md:text-4xl lg:text-5xl">
						Convention Information
					</h1>
					<p className="text-sm leading-relaxed text-muted-foreground">
						Everything you need to know about attending DanceMakers convention.
						From registration to event day, we've got you covered.
					</p>
				</div>
			</section>

			{/* Desktop-only floating quick links */}
			<div className="lg:grid lg:grid-cols-[minmax(0,1fr)_18rem] lg:items-start lg:gap-8 lg:px-6 xl:px-12">
				<div className="min-w-0">
					{/* Ages & Fees */}
					<section
						id="ages-fees"
						className="scroll-mt-36 bg-muted/30 py-8 md:py-12"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:mb-6 sm:text-2xl md:text-3xl">
								Ages & Fees
							</h2>

							<div className="-mx-6 overflow-x-auto px-6 md:mx-0 md:px-0">
								<table className="w-full border border-muted-foreground/30 text-xs sm:text-sm">
									<thead>
										<tr className="bg-muted/30">
											<th className="border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-4">
												Group
											</th>
											<th className="border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-4">
												Age
											</th>
											<th className="border-b border-r border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-4">
												Fee
											</th>
											<th className="border-b border-muted-foreground/30 p-2 text-left font-display uppercase sm:p-4">
												Late Fee
											</th>
										</tr>
									</thead>
									<tbody>
										<tr>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												Teacher
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												18+
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 font-bold sm:p-4">
												$340
											</td>
											<td className="border-b border-muted-foreground/30 p-2 sm:p-4">
												$20
											</td>
										</tr>
										<tr className="bg-muted/10">
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												Assistants
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												14+
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 font-bold sm:p-4">
												$330
											</td>
											<td className="border-b border-muted-foreground/30 p-2 sm:p-4">
												$20
											</td>
										</tr>
										<tr>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												Senior
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												14+
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 font-bold sm:p-4">
												$325
											</td>
											<td className="border-b border-muted-foreground/30 p-2 sm:p-4">
												$20
											</td>
										</tr>
										<tr className="bg-muted/10">
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												Teen
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												11-13
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 font-bold sm:p-4">
												$325
											</td>
											<td className="border-b border-muted-foreground/30 p-2 sm:p-4">
												$20
											</td>
										</tr>
										<tr>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												Junior
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 sm:p-4">
												7-10
											</td>
											<td className="border-b border-r border-muted-foreground/30 p-2 font-bold sm:p-4">
												$325
											</td>
											<td className="border-b border-muted-foreground/30 p-2 sm:p-4">
												$20
											</td>
										</tr>
										<tr className="bg-muted/10">
											<td className="border-r border-muted-foreground/30 p-2 sm:p-4">
												Camp Dance
											</td>
											<td className="border-r border-muted-foreground/30 p-2 sm:p-4">
												3-6
											</td>
											<td className="border-r border-muted-foreground/30 p-2 font-bold sm:p-4">
												$215
											</td>
											<td className="p-2 sm:p-4">$20</td>
										</tr>
									</tbody>
								</table>
							</div>

							<div
								id="ages-fees-overview"
								className="mt-4 max-w-4xl scroll-mt-36 space-y-3 text-xs leading-relaxed text-muted-foreground sm:mt-6 sm:space-y-4 sm:text-sm"
							>
								<p>
									<span className="font-bold text-foreground">
										All ages for convention and competition are as of January 1,
										2027.
									</span>{' '}
									The above age levels are guidelines to our DanceMakers
									convention experience. Each dancer will be issued a wristband
									that reflects their assigned ballroom for class and gives them
									access to that room only.
								</p>
								<p>
									Teachers are more than welcome to move dancers to a different
									age category for convention. This can be done online during
									the registration process or on-site. If done on-site, the
									wristband must be presented at registration by a registered
									teacher.
								</p>
								<p className="italic">
									Dance studios may apply their own additional administrative
									and overhead fees.
								</p>
								<p>
									One day and college rates are available. Please contact the
									DanceMakers home office for more info. Dancers that are
									registered in the competition must be registered for the full
									weekend and are not eligible for these discounts.
								</p>
							</div>
						</div>
					</section>

					{/* Registration Details */}
					<section
						id="registration-details"
						className="scroll-mt-36 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-6 font-display text-xl font-bold uppercase sm:mb-8 sm:text-2xl md:text-3xl">
								Registration Details
							</h2>

							<div className="grid max-w-6xl gap-4 sm:gap-6 md:grid-cols-2">
								<div id="registration-opening" className="scroll-mt-36">
									<InfoCard title="Registration Opening">
										<p className="mb-3">
											Again this season DanceMakers will stagger registration
											opening dates. Please check your city page to see your
											exact opening date! They range from{' '}
											<span className="font-bold text-foreground">
												July 20 - July 30, 2026
											</span>
											.
										</p>
										<p>
											We will be requiring a non-refundable deposit for
											competition routines. When you begin your registration, it
											will prompt you to enter the amount of routines you are
											bringing to compete. The deposit fee is{' '}
											<span className="font-bold text-foreground">
												$50 per routine
											</span>{' '}
											and is nontransferable to other events.
										</p>
									</InfoCard>
								</div>

								<div id="important-notice" className="scroll-mt-36">
									<InfoCard title="Important Notice">
										<p className="mb-3 font-bold text-foreground">
											Convention and competition could sell out!
										</p>
										<p className="mb-3">
											The majority of our competitions have sold out in the past
											and it is recommended to complete the registration process
											as soon as possible.
										</p>
										<p>
											Due to competitions selling out, we have competition
											limitations in place. Please see your city page for
											specific details.
										</p>
									</InfoCard>
								</div>

								<div id="registration-process" className="scroll-mt-36">
									<InfoCard title="Registration Process">
										<p className="mb-3">
											Registrations must be completed via our{' '}
											<a
												className="font-bold text-primary hover:underline"
												href="https://dancemakersinc.io"
												target="_blank"
												rel="noopener noreferrer"
											>
												online registration system
											</a>{' '}
											and should be received by event's cutoff date to avoid
											late fees.
										</p>
										<p className="mb-3">
											After the registration process is completed you will
											receive a basic confirmation email confirming we have
											received your registration.
										</p>
										<p>
											<span className="font-bold text-foreground">
												10 days before the event
											</span>{' '}
											all of the detailed information will be released on our
											website and emailed to the studio email address on
											account.
										</p>
									</InfoCard>
								</div>

								<div id="registration-kits" className="scroll-mt-36">
									<InfoCard title="Registration Kits">
										<p className="mb-3">
											Registrations with{' '}
											<span className="font-bold text-foreground">
												10 or more dancers
											</span>{' '}
											registered will receive their registration kits in the
											mail pre-event. These kits will be delivered by the
											Wednesday before the event.
										</p>
										<p className="mb-3">
											For studio and independent registrations with{' '}
											<span className="font-bold text-foreground">
												9 dancers or less
											</span>
											, we will have your registration kit available on site for
											pick up at registration.
										</p>
										<p className="mb-3">
											If you have 9 dancers or less registered and would like
											your registration kit shipped, there will be a $20
											shipping and handling fee applied.
										</p>
										<p className="font-bold text-foreground">
											Registration kits will include scholarship numbers,
											wristbands, your teacher handbook and a complimentary
											program.
										</p>
									</InfoCard>
								</div>

								<div id="vip-lanyards" className="scroll-mt-36">
									<InfoCard title="VIP Lanyards">
										<p>
											Each registered teacher will receive a VIP lanyard to help
											faculty, staff, and other studio owners easily recognize
											you. Not required to be worn, but encouraged. Lanyards do
											not replace your designated wristband for classroom entry.
										</p>
									</InfoCard>
								</div>

								<div id="important-requirements" className="scroll-mt-36">
									<InfoCard title="Important Requirements">
										<ul className="space-y-2">
											<li className="flex items-start gap-2">
												<span className="mt-0.5 text-primary">•</span>
												<span>
													Dancers are not required to compete to participate in
													convention.
												</span>
											</li>
											<li className="flex items-start gap-2">
												<span className="mt-0.5 text-primary">•</span>
												<span className="font-bold text-foreground">
													All dancers are required to be registered for the full
													convention in order to participate in competition.
												</span>
											</li>
											<li className="flex items-start gap-2">
												<span className="mt-0.5 text-primary">•</span>
												<span>
													Independent registrations will be accepted for
													convention only. These dancers are not eligible to
													compete.
												</span>
											</li>
											<li className="flex items-start gap-2">
												<span className="mt-0.5 text-primary">•</span>
												<span>
													Dancers that attend a studio, but are attending
													DanceMakers without the studio must have the studio
													complete their registration under the studio account
													prior to the event.
												</span>
											</li>
										</ul>
									</InfoCard>
								</div>

								<div id="liability-waiver" className="scroll-mt-36">
									<InfoCard title="Liability Waiver">
										<p className="mb-3">
											When submitting a registration, the studio is stating that
											during the event{' '}
											<span className="font-bold text-foreground">
												DanceMakers Inc will not be held responsible for any
												loss of property and/or injury
											</span>
											. You can sign the waiver{' '}
											<a
												className="font-bold text-primary hover:underline"
												href="https://dancemakersinc.io/waiver"
												target="_blank"
												rel="noopener noreferrer"
											>
												here
											</a>
											.
										</p>
										<p className="mb-3">
											To help keep our event safe and running smoothly, all
											dancers and teachers must have a completed waiver on file
											before receiving their event wristband. If a waiver has
											not been completed prior to the event registration cutoff,
											the attendee's wristband will be held and made available
											for the studio owner or teacher to pick up at on-site
											registration.
										</p>
										<p>
											For studios receiving registration packets by mail, only
											wristbands for dancers with completed waivers will be
											included in the shipment. Independent registrations may
											pick up their own wristband at registration after
											completing their waiver.
										</p>
									</InfoCard>
								</div>
							</div>
						</div>
					</section>

					{/* Payment Information */}
					<section
						id="payment-information"
						className="scroll-mt-36 bg-muted/30 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-6 font-display text-xl font-bold uppercase sm:mb-8 sm:text-2xl md:text-3xl">
								Payment Information
							</h2>

							<div className="grid max-w-6xl gap-4 sm:gap-6 md:grid-cols-2">
								<div id="payment-deadlines" className="scroll-mt-36">
									<InfoCard title="Payment Deadlines">
										<p className="mb-3">
											Registration and payment must be received before the
											attending event's cutoff date.{' '}
											<span className="font-bold text-foreground">
												Competition routines will be removed if payment is not
												received by this date.
											</span>
										</p>
										<p>
											If registration is received after the cutoff date, there
											will be a{' '}
											<span className="font-bold text-foreground">
												$20 late fee
											</span>{' '}
											applied to each attendee.
										</p>
									</InfoCard>
								</div>

								<div id="payment-methods" className="scroll-mt-36">
									<InfoCard title="Payment Methods">
										<p className="mb-3">
											<span className="font-bold text-foreground">
												Before cutoff date:
											</span>{' '}
											Credit cards and checks are accepted. Checks should be
											made payable to DanceMakers Inc., PO BOX 1836, Charleston,
											SC 29402.
										</p>
										<p>
											<span className="font-bold text-foreground">
												After cutoff date:
											</span>{' '}
											All payments made after the cutoff date (pre-event or on
											site) must be made by credit card, cashier's check or
											money order.
										</p>
									</InfoCard>
								</div>

								<div id="refund-policy" className="scroll-mt-36 md:col-span-2">
									<InfoCard title="Refund Policy">
										<p>
											<span className="font-bold text-foreground">
												There are no refunds.
											</span>{' '}
											Studio credits are available at the discretion of
											DanceMakers Inc.
										</p>
									</InfoCard>
								</div>
							</div>
						</div>
					</section>

					{/* Discounts & Promos */}
					<section
						id="discounts-benefits"
						className="scroll-mt-36 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-6 font-display text-xl font-bold uppercase sm:mb-8 sm:text-2xl md:text-3xl">
								Discounts & Benefits
							</h2>

							<div className="grid max-w-6xl gap-4 sm:gap-6 md:grid-cols-2">
								<div id="teacher-benefits" className="scroll-mt-36">
									<InfoCard title="Teacher Benefits">
										<div className="space-y-3">
											<div>
												<p className="mb-1 font-bold text-primary">
													Free Registration
												</p>
												<p>
													There is a $34 credit for each paid student toward
													teacher registration at each attending city, which
													equals{' '}
													<span className="font-bold text-foreground">
														1 FREE teacher for every 10 paid students
													</span>
													.
												</p>
											</div>

											<div>
												<p className="mb-1 font-bold text-primary">
													Studio Owner Benefit
												</p>
												<p>
													DanceMakers allows studio owners to attend for free!
													When registering, please choose the studio owner
													option to remove those fees.
												</p>
											</div>

											<div>
												<p className="mb-1 font-bold text-primary">
													Additional Cities
												</p>
												<p>
													For any teachers attending another city within the
													season, we'd love to have you attend on us! Please
													reach out to the home office to apply this for any
													additional cities.
												</p>
											</div>

											<div>
												<p className="mb-1 font-bold text-primary">
													VIP Events
												</p>
												<p>
													All teachers are welcome to attend the VIP events of
													the weekend such as the meet and greet in the
													teacher's ballroom on Saturday AM as well as the
													private teacher's luncheon on Sunday.
												</p>
											</div>

											<div>
												<p className="mb-1 font-bold text-primary">
													Complimentary Coffee Mug
												</p>
												<p>
													All registered teachers will receive a complimentary
													DanceMakers coffee mug! This coffee mug will get you
													coffee on us all weekend! Simply present this mug to
													have a refill of regular or decaf coffee and/or hot
													tea.
												</p>
											</div>

											<p className="mt-4 border-t border-muted-foreground/30 pt-3 text-xs italic">
												All teacher benefits are for Teachers only, not
												Assistant Teachers.
											</p>
										</div>
									</InfoCard>
								</div>

								<div id="attendee-discounts" className="scroll-mt-36">
									<InfoCard title="Attendee Discounts">
										<div>
											<p className="mb-2 font-bold text-primary">
												Second City Discount
											</p>
											<p>
												Any paid dancer attending a second Regional city will
												receive a{' '}
												<span className="font-bold text-foreground">
													25% discount
												</span>{' '}
												on their convention fees. When registering for an
												additional city, click "Second City Discount" under
												scholarships and DanceMakers will approve the discount.
											</p>
										</div>
									</InfoCard>
								</div>
							</div>
						</div>
					</section>

					{/* Event Information */}
					<section
						id="event-information"
						className="scroll-mt-36 bg-muted/30 py-10 md:py-16"
					>
						<div className="container px-6 md:px-16">
							<h2 className="mb-6 font-display text-xl font-bold uppercase sm:mb-8 sm:text-2xl md:text-3xl">
								Event Information
							</h2>

							<div className="grid max-w-6xl gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3">
								<div id="wristbands-entry" className="scroll-mt-36">
									<InfoCard title="Wristbands & Entry">
										<p className="mb-3">
											In order to participate and/or watch DanceMakers
											convention classes all attendees must have a wristband
											(teacher/dancer).{' '}
											<span className="font-bold text-foreground">
												Competition is open to anyone who would like to watch!
												No wristband required.
											</span>
										</p>
										<p className="mb-3">
											Wristbands for entry into ballroom must be worn throughout
											the convention and competition. Please do not remove
											wristband.
										</p>
										<p>
											If replacement wristband is needed, the old wristband must
											be presented at registration.
										</p>
									</InfoCard>
								</div>

								<div id="ballroom-guidelines" className="scroll-mt-36">
									<InfoCard title="Ballroom Guidelines">
										<p className="mb-3">
											During the convention setting there are{' '}
											<span className="font-bold text-foreground">
												no oversized dance bags or luggage allowed in the
												ballroom
											</span>
											. Keep all belongings as far back against the walls as
											possible. This is to ensure that all the dancers have
											plenty of space.
										</p>
										<p>
											We no longer offer observer bands for classes. The only
											exception to this is our Camp Dance room, which is our
											pre-school program.
										</p>
									</InfoCard>
								</div>

								<div id="teacher-guidelines" className="scroll-mt-36">
									<InfoCard title="Teacher Guidelines">
										<p className="mb-3">
											Teachers are more than welcome to observe and/or
											participate in all ballrooms. When dancing in student
											classrooms, we ask you dance to the side or in the back of
											the room behind the student dancers.
										</p>
										<p className="font-bold text-foreground">
											Teachers are not eligible to participate in competition.
										</p>
									</InfoCard>
								</div>

								<div id="assistant-teacher-guidelines" className="scroll-mt-36">
									<InfoCard title="Assistant Teacher Guidelines">
										<p className="mb-3">
											Assistant Teachers are more than welcome to observe and/or
											participate in all ballrooms. When dancing in the younger
											student classrooms, we ask you dance to the side or in the
											back of the room behind the student dancers. This is not
											applied in the Senior classroom.
										</p>
										<p className="font-bold text-foreground">
											Assistant Teachers are more than welcome to participate in
											competition as well as the Senior Audition Class.
										</p>
									</InfoCard>
								</div>

								<div id="etiquette-dress-code" className="scroll-mt-36">
									<InfoCard title="Etiquette & Dress Code">
										<p className="mb-3">
											All dancers must wear cover-up and shoes when outside the
											ballroom area. No tap shoes or bare feet outside
											ballrooms.
										</p>
										<p>
											Please be respectful of other guests in hotel and public
											spaces.
										</p>
									</InfoCard>
								</div>

								<div id="media-photography" className="scroll-mt-36">
									<InfoCard title="Media & Photography">
										<p className="mb-3">
											Choreographers and all attendees automatically give their
											permission to DanceMakers to use all photos and videos
											from convention and/or competition for advertising,
											publicity, and professional use.
										</p>
										<p className="mb-3">
											Photo and video during convention are allowed. Please
											remember to be respectful of the class.
										</p>
										<p>
											All photos posted to our social media platforms will be
											shared complimentary with all registered studios
											post-event. Photos will range from conventions,
											competitions, awards, etc.{' '}
										</p>
									</InfoCard>
								</div>
							</div>
						</div>
					</section>

					{/* Contact CTA */}
					<section
						id="questions"
						className="relative z-50 scroll-mt-36 bg-background py-10 md:py-16"
					>
						<div className="container px-6 text-center md:px-16">
							<h2 className="mb-4 font-display text-xl font-bold uppercase sm:text-2xl md:text-3xl">
								Questions?
							</h2>
							<p className="mx-auto mb-6 max-w-2xl text-sm text-muted-foreground">
								Have questions about convention registration or guidelines? Our
								team is here to help!
							</p>
							<a
								href="/contact"
								className="inline-block border border-primary px-8 py-3 text-sm font-bold uppercase tracking-wider text-primary transition-colors hover:bg-primary/90"
							>
								Contact Us
							</a>
						</div>
					</section>
				</div>

				<aside className="hidden lg:sticky lg:top-32 lg:block lg:self-start">
					<div className="relative">
						<div className="max-h-[calc(100vh-9rem)] overflow-y-auto pr-1">
							<QuickLinksPanel />
						</div>
					</div>
				</aside>
			</div>
		</div>
	)
}
